import React from 'react';

import { link, routes } from '@aline/routes';
import { Typography } from '@mui/material';
import { ChartColumnBig, Kanban, List as ListIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useMatch, useNavigate } from 'react-router-dom';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { SelectButton } from 'src/components/generic/SelectButton';
import { makeStyles } from 'tss-react/mui';

const WORKFLOW_MODES = {
  board: 'board',
  insights: 'insights',
  list: 'list',
} as const;

type WorkflowMode = (typeof WORKFLOW_MODES)[keyof typeof WORKFLOW_MODES];

interface WorkflowModeConfig {
  Icon: LucideIcon;
  label: string;
  toRoute: () => string;
}

const MODE_CONFIGS: Record<WorkflowMode, WorkflowModeConfig> = {
  [WORKFLOW_MODES.board]: { Icon: Kanban, label: 'Board', toRoute: link.WorkflowBoard },
  [WORKFLOW_MODES.insights]: {
    Icon: ChartColumnBig,
    label: 'Insights',
    toRoute: link.WorkflowInsights,
  },
  [WORKFLOW_MODES.list]: { Icon: ListIcon, label: 'List', toRoute: link.WorkflowList },
};

const MODE_ORDER: WorkflowMode[] = [
  WORKFLOW_MODES.list,
  WORKFLOW_MODES.board,
  WORKFLOW_MODES.insights,
];

const useStyles = makeStyles()(theme => ({
  button: {
    /**
     * Each segment runs to its neighbour, so its own corners stay square and the
     * container's `overflow: hidden` rounds the two outer ones for it. Doubled
     * specificity because `SelectButton` rounds every corner by default.
     */
    '&&': {
      borderRadius: 0,
    },
    '&:not(:first-of-type)': {
      borderLeft: `1px ${theme.appColors.rule} solid`,
    },
    gap: theme.spacing(0.75),
    height: '100%',
    paddingInline: theme.spacing(1.25),
  },
  root: {
    // Segments fill the control's full height rather than floating inside it.
    alignItems: 'stretch',
    backgroundColor: theme.palette.background.paper,
    border: `1px ${theme.appColors.rule} solid`,
    borderRadius: 8,
    display: 'inline-flex',
    // The three modes are one control, so it holds its width as the toolbar tightens.
    flexShrink: 0,
    height: 32,
    overflow: 'hidden',
  },
}));

interface WorkflowModeSwitcherProps {
  /** Drops the labels for icons and tooltips when the toolbar is too tight to carry them. */
  compact: boolean;
}

/**
 * The single switch between the three Workflows modes. Insights is a peer of List
 * and Board — not a panel on either — so this is the only place the mode changes.
 */
export function WorkflowModeSwitcher({ compact }: WorkflowModeSwitcherProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const isBoard = Boolean(useMatch({ end: false, path: routes.WorkflowBoard }));
  const isInsights = Boolean(useMatch({ end: false, path: routes.WorkflowInsights }));
  const activeMode = getActiveWorkflowMode({ isBoard, isInsights });

  return (
    <div className={classes.root} role="group">
      {MODE_ORDER.map(mode => {
        const { Icon, label, toRoute } = MODE_CONFIGS[mode];
        const isActive = mode === activeMode;

        return (
          <AlineTooltip
            key={mode}
            arrow
            disableHoverListener={!compact}
            placement="top"
            title={label}
          >
            <SelectButton
              aria-label={label}
              aria-pressed={isActive}
              className={classes.button}
              selected={isActive}
              onClick={() => {
                if (isActive) return;
                navigate(toRoute());
              }}
            >
              <Icon size={16} />
              {!compact && <Typography variant="subtitle2">{label}</Typography>}
            </SelectButton>
          </AlineTooltip>
        );
      })}
    </div>
  );
}

function getActiveWorkflowMode(options: { isBoard: boolean; isInsights: boolean }): WorkflowMode {
  if (options.isBoard) return WORKFLOW_MODES.board;
  if (options.isInsights) return WORKFLOW_MODES.insights;
  return WORKFLOW_MODES.list;
}
